/**
 * locale.js — the SINGLE source of truth for user-facing date, time and
 * currency formatting across PolkDesQ.
 *
 * Rules (see /app/memory/CODING_STANDARDS.md):
 *   - Raw `.toLocaleString()` / ad-hoc `₹`/`$` concatenation in JSX is a defect.
 *     Always route through these helpers.
 *
 * Timezone contract:
 *   - ISO strings and epoch numbers are ABSOLUTE INSTANTS and are converted
 *     to the target timezone (default Asia/Calcutta) before display.
 *   - `Date` objects are treated as ALREADY-LOCALISED wall-clock values
 *     (this codebase builds them via `nowInTz()`), and are formatted using
 *     their own components — no second timezone shift.
 *   - "HH:mm" / "HH:mm:ss" strings are wall-clock times (no tz), reformatted
 *     to 12-hour. "YYYY-MM-DD" strings are calendar dates (no tz).
 *
 * Invalid vs absent:
 *   - `null` / `undefined` / `''` is ABSENT → currency renders "—", dates render "".
 *   - A non-empty value that cannot be parsed is INVALID → throws a clear Error
 *     (so the bug surfaces loudly instead of silently rendering "Invalid Date").
 *
 * Currency:
 *   - INR uses Indian (lakh/crore) grouping via `en-IN`; every other currency
 *     uses Western grouping via `en-US`.
 *   - 0 → "₹0" / "$0" (a real value). null/undefined → "—" (absent).
 *   - Negatives render with the sign before the symbol: "-₹3,50,000".
 */
import { format as dfFormat } from 'date-fns';

export const DEFAULT_TZ = 'Asia/Calcutta';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const CURRENCY_SYMBOLS = { INR: '\u20B9', USD: '$', AED: 'AED ', EUR: '\u20AC', GBP: '\u00A3' };
const ABSENT_PLACEHOLDER = '\u2014'; // em dash

const WALL_CLOCK_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
const CAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const isAbsent = (v) => v == null || v === '';

function toFinite(amount, fnName) {
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) throw new Error(`${fnName}: expected a finite number, received "${amount}"`);
  return n;
}

function wallClockTo12(h, min) {
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(min).padStart(2, '0')} ${period}`;
}

function absoluteDateParts(value, tz, fnName) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error(`${fnName}: cannot parse "${value}" into a date`);
  return new Intl.DateTimeFormat('en-GB', { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric' })
    .formatToParts(d)
    .reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
}

/** Grouped number string only (no symbol). INR → Indian grouping, else Western. */
export function formatNumber(amount, currency = 'INR') {
  if (isAbsent(amount)) return ABSENT_PLACEHOLDER;
  const n = toFinite(amount, 'formatNumber');
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return n.toLocaleString(locale, { maximumFractionDigits: 2 });
}

/** Currency with symbol. e.g. formatCurrency(350000) → "₹3,50,000". */
export function formatCurrency(amount, currency = 'INR') {
  if (isAbsent(amount)) return ABSENT_PLACEHOLDER;
  const n = toFinite(amount, 'formatCurrency');
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  const sym = CURRENCY_SYMBOLS[currency] || (currency ? `${currency} ` : '');
  const sign = n < 0 ? '-' : '';
  const grouped = Math.abs(n).toLocaleString(locale, { maximumFractionDigits: 2 });
  return `${sign}${sym}${grouped}`;
}

/** 12-hour time with AM/PM, always. Accepts "HH:mm", Date, or ISO/epoch. */
export function formatTime(value, { tz = DEFAULT_TZ } = {}) {
  if (isAbsent(value)) return '';
  if (typeof value === 'string') {
    const m = value.match(WALL_CLOCK_RE);
    if (m) {
      const h = Number(m[1]);
      const min = Number(m[2]);
      if (h > 23 || min > 59) throw new Error(`formatTime: invalid wall-clock time "${value}"`);
      return wallClockTo12(h, min);
    }
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error('formatTime: received an invalid Date');
    return dfFormat(value, 'h:mm a');
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error(`formatTime: cannot parse "${value}" into a time`);
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
}

/** Date as DD-MMM-YYYY (default). options: { includeYear=true, includeTime=false, tz }. */
export function formatDate(value, { includeYear = true, includeTime = false, tz = DEFAULT_TZ } = {}) {
  if (isAbsent(value)) return '';
  if (typeof value === 'string') {
    const dm = value.match(CAL_DATE_RE);
    if (dm) {
      const mon = MONTHS[Number(dm[2]) - 1];
      if (!mon) throw new Error(`formatDate: invalid month in "${value}"`);
      return includeYear ? `${dm[3]}-${mon}-${dm[1]}` : `${dm[3]}-${mon}`;
    }
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error('formatDate: received an invalid Date');
    let out = dfFormat(value, includeYear ? 'dd-MMM-yyyy' : 'dd-MMM');
    if (includeTime) out += `, ${formatTime(value, { tz })}`;
    return out;
  }
  const p = absoluteDateParts(value, tz, 'formatDate');
  let out = includeYear ? `${p.day}-${p.month}-${p.year}` : `${p.day}-${p.month}`;
  if (includeTime) out += `, ${formatTime(value, { tz })}`;
  return out;
}

/** Convenience: date + time. */
export function formatDateTime(value, options = {}) {
  return formatDate(value, { ...options, includeTime: true });
}

/** Relative phrasing: "just now", "in 3 hours", "tomorrow", "2 days ago". */
export function formatRelativeDate(value, baseDate = new Date(), { tz = DEFAULT_TZ } = {}) {
  if (isAbsent(value)) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error(`formatRelativeDate: cannot parse "${value}" into a date`);
  const base = baseDate instanceof Date ? baseDate : new Date(baseDate);
  if (Number.isNaN(base.getTime())) throw new Error('formatRelativeDate: invalid baseDate');

  const dayStr = (dt) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(dt);
  const toDayNum = (s) => { const [y, m, da] = s.split('-').map(Number); return Date.UTC(y, m - 1, da) / 86400000; };
  const dayDiff = toDayNum(dayStr(d)) - toDayNum(dayStr(base));

  if (dayDiff === 0) {
    const diffMs = d.getTime() - base.getTime();
    const absMs = Math.abs(diffMs);
    if (absMs < 60000) return 'just now';
    const future = diffMs > 0;
    if (absMs < 3600000) {
      const mins = Math.round(absMs / 60000);
      return future ? `in ${mins} minute${mins === 1 ? '' : 's'}` : `${mins} minute${mins === 1 ? '' : 's'} ago`;
    }
    const hrs = Math.round(absMs / 3600000);
    return future ? `in ${hrs} hour${hrs === 1 ? '' : 's'}` : `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  }
  if (dayDiff === 1) return 'tomorrow';
  if (dayDiff === -1) return 'yesterday';
  if (dayDiff > 1) return `in ${dayDiff} days`;
  return `${Math.abs(dayDiff)} days ago`;
}


/**
 * Canonical "today"/"tomorrow" as a YYYY-MM-DD calendar date in the given IANA
 * timezone (defaults to India / Asia-Calcutta per the product mandate).
 *
 * Use these INSTEAD of `new Date().toISOString().slice(0,10)` — that returns the
 * UTC date, which is wrong for ~5.5h every night in IST (00:00–05:30 IST is still
 * "yesterday" in UTC), silently mis-scheduling user-chosen dates.
 * (DATETIME-IST-TODAY-SWEEP-1)
 */
export function todayIST(tz = DEFAULT_TZ) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export function tomorrowIST(tz = DEFAULT_TZ) {
  // Pure calendar arithmetic off today's tz-local date — no DST/offset drift.
  const [y, m, d] = todayIST(tz).split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d));
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/**
 * A YYYY-MM-DD calendar date `days` after today in the given IANA timezone
 * (defaults to IST). Pure calendar arithmetic off today's tz-local date — no
 * DST/offset drift and no UTC-slice bug. Use for "snooze N days" / "due in N
 * days" instead of `new Date(); d.setDate(d.getDate()+n); d.toISOString().slice(0,10)`,
 * which silently produces the UTC date (wrong for 00:00–05:30 IST every night).
 * (DATETIME-IST-SWEEP-TAIL-PASS-1)
 */
export function addDaysIST(days, tz = DEFAULT_TZ) {
  const [y, m, d] = todayIST(tz).split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d));
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}
